import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

// ─── Types Jury ─────────────────────────────────────────────────────────────

export interface MembreJury {
  id: number;
  nom_complet: string;
  fonction: string;
  fonction_display: string;
  titre_academique: string;
  pole: number;
  pole_nom: string;
  ordre_affichage: number;
  actif: boolean;
}

export interface MembreJuryCreate {
  nom_complet: string;
  fonction: string;
  titre_academique?: string;
  pole: number;
  ordre_affichage?: number;
  actif?: boolean;
}

// ─── Types PV ───────────────────────────────────────────────────────────────

export interface PVMatiereStructure {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
}

export interface PVUEStructure {
  ue_id: number;
  code_ue: string;
  nom_ue: string;
  credits_total: number;
  matieres: PVMatiereStructure[];
}

export interface PVNoteEtudiant {
  note_examen: number | null;
  moyenne_ecue: number | null;
  absent: boolean;
}

export interface PVUEResultat {
  moyenne_ue: number | null;
  validee: boolean;
  credits_obtenus: number;
  compensable?: boolean;
  eliminatoire?: boolean;
  validee_par_compensation?: boolean;
}

export interface PVResultatSemestre {
  moyenne_semestre: number | null;
  credits_obtenus: number;
  credits_requis: number;
  valide: boolean;
  rang: number | null;
  decision: string;
  mention: string;
  /** 'normale' si validé sans rattrapage, 'rattrapage' si validé grâce à SR, '' sinon. */
  session_validation?: 'normale' | 'rattrapage' | '';
  // ═══ Champs supplémentaires en mode session='synthese' ═══
  moyenne_semestre_ordinaire?: number | null;
  valide_ordinaire?: boolean;
  decision_ordinaire?: string;
  rang_ordinaire?: number | null;
  moyenne_semestre_finale?: number | null;
  valide_finale?: boolean;
  decision_finale?: string;
}

export interface PVEtudiant {
  matricule: string;
  nom: string;
  prenoms: string;
  date_naissance: string | null;
  lieu_naissance: string;
  sexe: string;
  notes: Record<string, PVNoteEtudiant>;
  ues_results: Record<string, PVUEResultat>;
  resultat_semestre: PVResultatSemestre | null;
  /** True si l'étudiant a composé au moins une matière au rattrapage. */
  a_fait_rattrapage?: boolean;
}

export interface PVInfoClasse {
  classe_code: string;
  niveau: string;
  option_nom: string;
  option_code: string;
  pole_nom: string;
  pole_id: number | null;
  parcours_nom: string;
  cycle: string;
}

export interface PVStatistiques {
  effectif_total: number;
  nombre_admis: number;
  nombre_ajournes: number;
  taux_reussite: number;
  // Participation basée sur les copies scannées (nouveau — depuis 2026-07-06)
  nombre_participants?: number;
  nombre_absents?: number;
  taux_participation?: number;
  // ═══ Session rattrapage ═══
  /** Nombre d'étudiants convoqués au rattrapage (== nombre listés dans le PV). */
  effectif_convoques_rattrapage?: number;
  // ═══ Mode 'synthese' — statistiques bi-session ═══
  nombre_participants_ordinaire?: number;
  nombre_absents_ordinaire?: number;
  nombre_admis_ordinaire?: number;
  taux_reussite_ordinaire?: number;
  nombre_participants_rattrapage?: number;
  /** Admis grâce au rattrapage (nombre_admis_final - nombre_admis_ordinaire). */
  nombre_admis_rattrapage?: number;
}

export interface DonneesPV {
  info_classe: PVInfoClasse;
  annee_academique: string;
  semestre: number;
  session: string;
  ues_structure: PVUEStructure[];
  etudiants: PVEtudiant[];
  statistiques: PVStatistiques;
  jury: MembreJury[];
}

export type SessionPV = 'ordinaire' | 'rattrapage' | 'synthese';

export interface DonneesPVParams {
  classe_code: string;
  semestre: number;
  annee_academique: string;
  session: SessionPV;
}

export interface PVDeliberationRecord {
  id: number;
  classe_code: string;
  option_nom: string;
  pole_nom: string;
  parcours_nom: string;
  session: string;
  session_display: string;
  semestre: number;
  annee_academique: string;
  date_deliberation: string | null;
  effectif_total: number;
  nombre_admis: number;
  nombre_ajournes: number;
  taux_reussite: number;
  jury_snapshot: MembreJury[];
  imprime_par: number | null;
  imprime_par_nom: string | null;
  date_impression: string;
}

export interface PVHistoriqueResponse {
  total: number;
  ce_mois: number;
  ordinaires: number;
  rattrapages: number;
  pv_list: PVDeliberationRecord[];
}

// ─── Types Bottin des étudiants finalistes ────────────────────────────────

export interface BottinEtudiant {
  matricule: string;
  nom: string;
  prenoms: string;
  sexe: string;
  date_naissance: string | null;
  lieu_naissance: string;
  nationalite: string;
  parcours: string;
  annee_entree: string;
  telephone: string;
  email: string;
  moyenne_s1: number | null;
  moyenne_s2: number | null;
  moyenne_s3: number | null;
  moyenne_s4: number | null;
  moyenne_s5: number | null;
  moyenne_s6: number | null;
  /** Nombre de semestres sur 6 pour lesquels une moyenne 2i existe. */
  nb_semestres_2i: number;
}

export interface BottinRecapFiliere {
  parcours: string;
  niveau: string;
  effectif_total: number;
  dont_filles: number;
}

export interface DonneesBottin {
  annee_academique: string;
  cycle: string;
  campus: string;
  responsable_academique: string;
  effectif_total: number;
  etudiants: BottinEtudiant[];
  recapitulatif_par_filiere: BottinRecapFiliere[];
  schemas_historiques_utilises: string[];
}

export interface DonneesBottinParams {
  annee_academique: string;
  cycle?: 'licence';
  campus?: string;
  responsable_academique?: string;
}

// ─── API Slice ──────────────────────────────────────────────────────────────

export const pvDeliberationApi = createApi({
  reducerPath: 'pvDeliberationApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['MembreJury', 'PVDeliberation'],
  endpoints: (builder) => ({
    // Jury CRUD
    getMembresJury: builder.query<MembreJury[], { pole?: number; actif?: boolean }>({
      query: (params) => {
        const qs = new URLSearchParams();
        if (params.pole) qs.set('pole', String(params.pole));
        if (params.actif !== undefined) qs.set('actif', String(params.actif));
        return `/membres-jury/?${qs.toString()}`;
      },
      transformResponse: (response: any) => {
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['MembreJury'],
    }),
    createMembreJury: builder.mutation<MembreJury, MembreJuryCreate>({
      query: (body) => ({ url: '/membres-jury/', method: 'POST', body }),
      invalidatesTags: ['MembreJury'],
    }),
    updateMembreJury: builder.mutation<MembreJury, { id: number; data: Partial<MembreJuryCreate> }>({
      query: ({ id, data }) => ({ url: `/membres-jury/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: ['MembreJury'],
    }),
    deleteMembreJury: builder.mutation<void, number>({
      query: (id) => ({ url: `/membres-jury/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['MembreJury'],
    }),

    // PV Data — force le header X-Annee-Academique à l'année demandée pour que
    // le middleware bascule sur le bon schéma PostgreSQL (annee_YYYY_YYYY).
    // Sans cet override, la requête utilise l'année globale du localStorage et
    // renvoie des données vides quand l'utilisateur choisit une année archivée.
    getDonneesPV: builder.query<DonneesPV, DonneesPVParams>({
      query: ({ classe_code, semestre, annee_academique, session }) => ({
        url: `/pv-deliberations/donnees-pv/?classe_code=${classe_code}&semestre=${semestre}&annee_academique=${annee_academique}&session=${session}`,
        headers: { 'X-Annee-Academique': annee_academique },
      }),
    }),

    // PV Recording
    enregistrerPV: builder.mutation<PVDeliberationRecord, Record<string, unknown>>({
      query: (body) => ({ url: '/pv-deliberations/enregistrer-pv/', method: 'POST', body }),
      invalidatesTags: ['PVDeliberation'],
    }),

    // Bottin des étudiants finalistes (document ministériel) — même override header
    getDonneesBottin: builder.query<DonneesBottin, DonneesBottinParams>({
      query: ({ annee_academique, cycle = 'licence', campus, responsable_academique }) => {
        const qs = new URLSearchParams();
        qs.set('annee_academique', annee_academique);
        qs.set('cycle', cycle);
        if (campus) qs.set('campus', campus);
        if (responsable_academique) qs.set('responsable_academique', responsable_academique);
        return {
          url: `/pv-deliberations/donnees-bottin/?${qs.toString()}`,
          headers: { 'X-Annee-Academique': annee_academique },
        };
      },
    }),

    // Bottin de RÉINSCRIPTION (par niveau L1/L2, groupé par option)
    getDonneesBottinReinscription: builder.query<import('../components/PV/BottinReinscriptionBuilder').DonneesBottinReinscription, {
      niveau: 'L1' | 'L2'; annee_academique: string; campus?: string;
      responsable_academique?: string; seuil_eligibilite?: number;
    }>({
      query: ({ niveau, annee_academique, campus, responsable_academique, seuil_eligibilite }) => {
        const qs = new URLSearchParams();
        qs.set('niveau', niveau);
        qs.set('annee_academique', annee_academique);
        if (campus) qs.set('campus', campus);
        if (responsable_academique) qs.set('responsable_academique', responsable_academique);
        if (seuil_eligibilite != null) qs.set('seuil_eligibilite', String(seuil_eligibilite));
        return {
          url: `/pv-deliberations/donnees-bottin-reinscription/?${qs.toString()}`,
          headers: { 'X-Annee-Academique': annee_academique },
        };
      },
    }),

    // PV History
    getHistoriquePV: builder.query<PVHistoriqueResponse, {
      annee_academique?: string;
      session?: string;
      date_debut?: string;
      date_fin?: string;
    }>({
      query: (filters = {}) => {
        const qs = new URLSearchParams();
        if (filters.annee_academique) qs.set('annee_academique', filters.annee_academique);
        if (filters.session) qs.set('session', filters.session);
        if (filters.date_debut) qs.set('date_debut', filters.date_debut);
        if (filters.date_fin) qs.set('date_fin', filters.date_fin);
        const s = qs.toString();
        return `/pv-deliberations/historique-pv/${s ? '?' + s : ''}`;
      },
      providesTags: ['PVDeliberation'],
    }),
  }),
});

export const {
  useGetMembresJuryQuery,
  useCreateMembreJuryMutation,
  useUpdateMembreJuryMutation,
  useDeleteMembreJuryMutation,
  useLazyGetDonneesPVQuery,
  useEnregistrerPVMutation,
  useGetHistoriquePVQuery,
  useGetDonneesBottinQuery,
  useLazyGetDonneesBottinQuery,
  useLazyGetDonneesBottinReinscriptionQuery,
} = pvDeliberationApi;
